import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { streamChat, useSessionMessages } from '@/lib/api/chat';
import { deleteChatAttachment, uploadChatAttachment } from '@/lib/api/attachments';
import { Card } from '@/components/ui/Card';
import { QueryState } from '@/components/QueryState';
import { MessageBubble, type ChatMessage } from '@/features/chat/MessageBubble';
import { useAgentContext } from '@/features/agents/AgentLayout';
import type { AttachmentOut, AttachmentRef, ChatUsage } from '@/lib/api/types';

// Formats the chat composer accepts (spec 0015). Mirrors the backend allowlist.
const ATTACHMENT_ACCEPT = '.pdf,.docx,.xlsx,.csv,.xml,.json,.txt,.md';

/** The slim message-bound shape kept once an attachment has been sent. */
function toRef(att: AttachmentOut): AttachmentRef {
  return { id: att.id, filename: att.filename, format: att.format, size_bytes: att.size_bytes };
}

function newId(): string {
  return Math.random().toString(36).slice(2);
}

export function ChatPage() {
  // The agent UUID is resolved once by the layout from the URL slug (spec 0013).
  const { agentId } = useAgentContext();
  // Channel-triggered sessions (e.g. email, spec 0007) link here with
  // ?session=<id>: the transcript loads into this same chat view and any
  // follow-up message continues that session.
  const [searchParams] = useSearchParams();
  const sessionParam = searchParams.get('session') ?? undefined;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usage, setUsage] = useState<ChatUsage | null>(null);
  const [sessionId, setSessionId] = useState<string | undefined>(sessionParam);
  // A short, high-level status announced to screen readers (token-by-token
  // streaming into a live region would be far too noisy).
  const [liveStatus, setLiveStatus] = useState('');
  // Shown only when the user has scrolled up: a way back to the newest message
  // while a reply keeps streaming in below them.
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  // Attachments uploaded for the next message but not yet sent (spec 0015).
  // They upload the instant a file is chosen, opening/reusing a conversation.
  const [pendingAttachments, setPendingAttachments] = useState<AttachmentOut[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const history = useSessionMessages(sessionParam);
  // Seed the transcript once per linked session, including in-place
  // navigations (the panel lives on this page, so it may not remount).
  const loadedTranscriptRef = useRef<string | null>(null);
  useEffect(() => {
    if (sessionParam) setSessionId(sessionParam);
  }, [sessionParam]);
  useEffect(() => {
    if (!sessionParam || !history.data || loadedTranscriptRef.current === sessionParam) return;
    loadedTranscriptRef.current = sessionParam;
    setMessages(
      history.data
        .filter((m) => m.role !== 'system')
        .map((m) => ({
          id: m.id,
          role: m.role === 'user' ? 'user' : 'assistant',
          content: m.content,
          attachments: m.attachments,
        })),
    );
  }, [sessionParam, history.data]);

  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Only auto-scroll when the user is already at the bottom, so we never yank
  // the view away while they read earlier messages.
  const atBottomRef = useRef(true);

  function handleScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    atBottomRef.current = atBottom;
    // Offer a way back down whenever the user has scrolled up; tokens keep
    // arriving below without yanking their view (auto-scroll stays suppressed).
    setShowJumpToLatest(!atBottom && messages.length > 0);
  }

  function jumpToLatest() {
    const el = scrollRef.current;
    if (!el) return;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    atBottomRef.current = true;
    setShowJumpToLatest(false);
  }

  useEffect(() => {
    const el = scrollRef.current;
    if (el && atBottomRef.current) {
      el.scrollTo({ top: el.scrollHeight });
      setShowJumpToLatest(false);
    }
  }, [messages]);

  // Grow the composer with its content up to the CSS max-height, then scroll
  // inside it -- a fixed two-row box makes longer drafts hard to review.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);

  // Cancel any in-flight stream on unmount.
  useEffect(() => () => abortRef.current?.abort(), []);

  async function sendMessage() {
    const text = input.trim();
    if (!text || !agentId || isStreaming || isUploading) return;

    setError(null);
    setInput('');
    setLiveStatus('Assistant is responding.');

    // The backend binds these already-uploaded attachments to the user message
    // this send creates; show them on the bubble once the send goes through.
    const sentAttachments = pendingAttachments.map(toRef);
    const userId = newId();
    const userMessage: ChatMessage = { id: userId, role: 'user', content: text };
    const assistantId = newId();
    setMessages((prev) => [
      ...prev,
      userMessage,
      { id: assistantId, role: 'assistant', content: '' },
    ]);
    atBottomRef.current = true;

    const controller = new AbortController();
    abortRef.current = controller;
    setIsStreaming(true);

    let outcome: 'done' | 'error' | 'stopped' = 'done';
    try {
      await streamChat(
        { agent_id: agentId, session_id: sessionId, message: text },
        {
          signal: controller.signal,
          onToken: (chunk) => {
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + chunk } : m)),
            );
          },
          onSession: (sid) => setSessionId(sid),
          onUsage: (u) => setUsage(u),
          onError: (msg) => {
            outcome = 'error';
            setError(msg);
          },
        },
      );
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        outcome = 'stopped';
      } else {
        outcome = 'error';
        setError((err as Error).message);
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
      setLiveStatus(outcome === 'done' ? 'Assistant response complete.' : '');
    }

    // The message was sent (the attachments are now bound to it server-side):
    // surface them on the just-sent bubble and clear the composer's pending set.
    if (sentAttachments.length > 0) {
      setMessages((prev) =>
        prev.map((m) => (m.id === userId ? { ...m, attachments: sentAttachments } : m)),
      );
      setPendingAttachments([]);
    }
  }

  /** Upload every chosen file immediately, threading the (possibly new)
   * conversation id through so all uploads and the send share one session. */
  async function handleFilesSelected(e: ChangeEvent<HTMLInputElement>) {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0 || !agentId) return;
    const files = Array.from(fileList);
    // Allow re-selecting the same file later (browsers suppress an unchanged value).
    e.target.value = '';

    setAttachmentError(null);
    setIsUploading(true);
    // Track the session locally: setSessionId is async, so the second upload in
    // this loop would otherwise still see the old id and open a second session.
    let currentSession = sessionId;
    try {
      for (const file of files) {
        const uploaded = await uploadChatAttachment({
          agentId,
          sessionId: currentSession,
          file,
        });
        currentSession = uploaded.session_id;
        setSessionId(uploaded.session_id);
        setPendingAttachments((prev) => [...prev, uploaded]);
      }
    } catch (err) {
      // Leave the typed message untouched; only report the upload failure.
      setAttachmentError((err as Error).message);
    } finally {
      setIsUploading(false);
    }
  }

  async function handleRemoveAttachment(id: string) {
    setAttachmentError(null);
    try {
      await deleteChatAttachment(id);
      setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      setAttachmentError((err as Error).message);
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    void sendMessage();
  }

  function handleStop() {
    abortRef.current?.abort();
  }

  return (
    <div className="flex h-full flex-col">
      <Card className="flex min-h-0 flex-1 flex-col">
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="min-h-0 flex-1 overflow-auto pr-1"
          >
            <QueryState
              isLoading={history.isLoading}
              error={history.error}
              loadingLabel="Loading conversation"
            >
              {messages.length === 0 ? (
                <div className="flex h-full min-h-[16rem] flex-col items-center justify-center px-6 text-center">
                  <div className="mb-3 rounded-full bg-brand-50 p-3 text-brand-600">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="h-6 w-6"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8.625 9.75a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-700">Start the conversation</p>
                  <p className="mt-1 max-w-xs text-sm text-gray-500">
                    Type a message below, or use the + button to attach a document for the agent
                    to read.
                  </p>
                </div>
              ) : (
                <ul className="space-y-4">
                  {messages.map((m, i) => (
                    <MessageBubble
                      key={m.id}
                      message={m}
                      streaming={isStreaming && i === messages.length - 1}
                    />
                  ))}
                </ul>
              )}
            </QueryState>
          </div>

          {showJumpToLatest && (
            <button
              type="button"
              onClick={jumpToLatest}
              data-testid="chat-jump-latest"
              className="absolute inset-x-0 bottom-3 mx-auto flex w-max cursor-pointer items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-md transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 motion-reduce:transition-none"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-3.5 w-3.5"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25 12 15.75 4.5 8.25" />
              </svg>
              Jump to latest
            </button>
          )}
        </div>

        {/* Visually hidden, high-level status for assistive tech. */}
        <p role="status" aria-live="polite" className="sr-only">
          {liveStatus}
        </p>

        {error && (
          <p
            role="alert"
            className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            data-testid="chat-error"
          >
            {error}
          </p>
        )}

        {usage && (
          <p className="mt-3 text-xs text-gray-500">
            Tokens - input: {usage.input_tokens.toLocaleString()}, output:{' '}
            {usage.output_tokens.toLocaleString()}
            {usage.model ? ` (${usage.model})` : ''}
          </p>
        )}

        {attachmentError && (
          <p
            role="alert"
            aria-live="polite"
            className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            data-testid="chat-attachment-error"
          >
            {attachmentError}
          </p>
        )}

        <form onSubmit={handleSubmit} className="mt-4">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ATTACHMENT_ACCEPT}
            onChange={(e) => void handleFilesSelected(e)}
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            data-testid="chat-attach-input"
          />
          {/* One unified composer surface: file chips, the borderless input, and
              the icon actions all live inside a single rounded, focus-lit box. */}
          <div className="flex flex-col gap-2 rounded-3xl border border-gray-300 bg-white px-2.5 py-2 shadow-sm transition-colors focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500 motion-reduce:transition-none">
            {pendingAttachments.length > 0 && (
              <ul className="flex flex-wrap gap-2 px-1 pt-1" aria-label="Attached files">
                {pendingAttachments.map((att) => (
                  <li
                    key={att.id}
                    data-testid="chat-attachment-chip"
                    className="inline-flex max-w-[18rem] items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 py-1 pl-2 pr-1 text-xs text-gray-700"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="h-4 w-4 shrink-0 text-gray-400"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                      />
                    </svg>
                    <span className="truncate">{att.filename}</span>
                    <button
                      type="button"
                      onClick={() => void handleRemoveAttachment(att.id)}
                      aria-label={`Remove ${att.filename}`}
                      data-testid="chat-attachment-remove"
                      className="flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gray-400"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="h-3 w-3"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-end gap-1">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!agentId || isUploading}
                aria-label="Attach files"
                data-testid="chat-attach-button"
                className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11"
              >
                {isUploading ? (
                  <svg
                    className="h-5 w-5 animate-spin motion-reduce:animate-none"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="h-5 w-5"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                )}
              </button>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void sendMessage();
                  }
                }}
                aria-label="Message"
                placeholder="Message the agent..."
                className="block max-h-40 min-h-[2.25rem] flex-1 resize-none border-0 bg-transparent px-1 py-2 text-base placeholder:text-gray-400 focus:outline-none focus:ring-0 sm:text-sm"
                disabled={!agentId}
                data-testid="chat-input"
              />
              {isStreaming ? (
                <button
                  type="button"
                  onClick={handleStop}
                  aria-label="Stop generating"
                  data-testid="chat-stop"
                  className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-gray-800 text-white transition-colors hover:bg-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 motion-reduce:transition-none [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11"
                >
                  <span className="h-2.5 w-2.5 rounded-[2px] bg-current" aria-hidden="true" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!input.trim() || !agentId || isUploading}
                  aria-label="Send message"
                  data-testid="chat-send"
                  className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-brand-600 text-white transition-colors hover:bg-brand-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 motion-reduce:transition-none [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2.2}
                    stroke="currentColor"
                    className="h-5 w-5"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 19.5V4.5m0 0-6.75 6.75M12 4.5l6.75 6.75"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
}
